import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sword, ShieldAlert, Lock, RefreshCw, AlertCircle, Crown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Pro Debate Panel — Cross-Examination (free) + Arbiter (premium-only).
 * Layered on top of an existing completed debate. Never modifies synthesis.
 */
export const ProDebatePanel = ({ discussionId, debatersMeta = {}, isPremium = false }) => {
  const [status, setStatus] = useState(null);
  const [cross, setCross] = useState(null);
  const [arbiter, setArbiter] = useState(null);
  const [loading, setLoading] = useState(null); // "cross" | "arbiter"
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/debate/${discussionId}/pro-protocol-status`);
      setStatus(r.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[ProPanel] status failed:", err?.message);
    }
  };

  useEffect(() => { fetchStatus(); /* eslint-disable-next-line */ }, [discussionId]);

  const runCrossExam = async () => {
    setLoading("cross");
    setError(null);
    try {
      const r = await axios.post(`${API}/debate/${discussionId}/cross-examine`);
      setCross(r.data.cross_examination);
      toast.success("Kruisverhoor voltooid");
      fetchStatus();
    } catch (err) {
      setError(err?.response?.data?.detail || "Kruisverhoor mislukt.");
    } finally {
      setLoading(null);
    }
  };

  const runArbiter = async () => {
    setLoading("arbiter");
    setError(null);
    try {
      const r = await axios.post(`${API}/debate/${discussionId}/arbiter`, { is_premium: isPremium });
      setArbiter(r.data.arbiter_report);
      toast.success("Arbiter-rapport gereed");
      fetchStatus();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Arbiter mislukt.";
      if (status === 402) {
        setError(detail);
      } else {
        setError(detail);
      }
    } finally {
      setLoading(null);
    }
  };

  const roleName = (key) => debatersMeta?.[key]?.name || key;

  return (
    <section className="mb-8 print-block" data-testid="pro-debate-panel">
      <h2 className="text-xs font-mono uppercase text-gray-500 mb-3">Pro debate-protocol</h2>

      {/* Cross-Examination */}
      <div className="bg-white border-2 border-amber-300 p-5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Sword className="w-4 h-4 text-amber-600" />
            <h3 className="font-bold text-sm">Kruisverhoor (Cross-Examination)</h3>
          </div>
          {(cross || status?.has_cross_examination) && (
            <button onClick={runCrossExam} disabled={loading === "cross"}
              className="no-print text-[11px] text-amber-700 hover:underline inline-flex items-center gap-1"
              data-testid="rerun-cross-exam"
              title="Opnieuw uitvoeren"
            >
              {loading === "cross" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Opnieuw
            </button>
          )}
        </div>
        <p className="text-xs text-gray-600 mb-3">
          Elke specialist stelt één scherpe vraag aan elke collega. Doel: blootleggen waar disciplines elkaar
          echt tegenspreken — geen polderen.
        </p>

        {!cross && !status?.has_cross_examination && (
          <button onClick={runCrossExam} disabled={loading === "cross"}
            className="inline-flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold disabled:opacity-50 transition-colors"
            data-testid="run-cross-exam-btn"
          >
            {loading === "cross" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sword className="w-3 h-3" />}
            {loading === "cross" ? "Bezig met kruisverhoor..." : "Start kruisverhoor"}
          </button>
        )}

        {cross?.questions?.length > 0 && (
          <div className="space-y-2 mt-2" data-testid="cross-exam-questions">
            {cross.questions.map((q, idx) => (
              <div key={`q-${idx}-${q.asker}-${q.target}`} className="border-l-2 border-amber-400 pl-3 py-1">
                <p className="text-[10px] font-mono text-gray-500 uppercase">
                  {roleName(q.asker)} → {roleName(q.target)}
                </p>
                <p className="text-xs mt-1">{q.question}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Arbiter */}
      <div className="bg-white border-2 border-purple-300 p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-purple-600" />
            <h3 className="font-bold text-sm">Arbiter-rapport</h3>
            <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-sm">
              <Crown className="w-2.5 h-2.5" /> PREMIUM
            </span>
          </div>
          {(arbiter || status?.has_arbiter_report) && (
            <button onClick={runArbiter} disabled={loading === "arbiter" || !isPremium}
              className="no-print text-[11px] text-purple-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="rerun-arbiter"
            >
              {loading === "arbiter" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Opnieuw
            </button>
          )}
        </div>

        <p className="text-xs text-gray-600 mb-3">
          Een onafhankelijke arbiter scant het debat op hallucinaties, drogredenen en weggemoffelde meningsverschillen.
        </p>

        {!arbiter && !status?.has_arbiter_report && (
          <button onClick={runArbiter} disabled={loading === "arbiter"}
            className="inline-flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold disabled:opacity-50 transition-colors"
            data-testid="run-arbiter-btn"
          >
            {isPremium ? (
              <>
                {loading === "arbiter" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldAlert className="w-3 h-3" />}
                {loading === "arbiter" ? "Arbiter beoordeelt..." : "Start arbiter"}
              </>
            ) : (
              <>
                <Lock className="w-3 h-3" />
                Premium vereist
              </>
            )}
          </button>
        )}

        {arbiter && (
          <div className="space-y-3 mt-2" data-testid="arbiter-report">
            {arbiter.arbiter_verdict && (
              <div className="bg-purple-50 border border-purple-200 p-3">
                <p className="text-[10px] font-mono uppercase text-purple-600 mb-1">Verdict</p>
                <p className="text-xs font-medium">{arbiter.arbiter_verdict}</p>
                {arbiter.confidence_in_synthesis && (
                  <p className="text-[10px] font-mono text-gray-500 mt-2">
                    Vertrouwen in synthese: <span className="font-bold">{arbiter.confidence_in_synthesis}</span>
                  </p>
                )}
              </div>
            )}

            {arbiter.hallucinations?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase text-red-600 mb-1">Hallucinaties / onbewezen claims</p>
                {arbiter.hallucinations.map((h, i) => (
                  <div key={`hal-${i}`} className="text-xs mb-1 pl-2 border-l-2 border-red-300">
                    <span className="text-[10px] font-mono text-gray-500">{roleName(h.speaker)}:</span> {h.claim}
                    {h.why_problematic && <p className="text-[11px] text-gray-600 mt-0.5">↳ {h.why_problematic}</p>}
                  </div>
                ))}
              </div>
            )}

            {arbiter.logical_fallacies?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase text-orange-600 mb-1">Drogredenen</p>
                {arbiter.logical_fallacies.map((f, i) => (
                  <div key={`fal-${i}`} className="text-xs mb-1 pl-2 border-l-2 border-orange-300">
                    <span className="text-[10px] font-mono text-gray-500">{roleName(f.speaker)} — {f.fallacy_type}:</span>
                    <p className="text-[11px] text-gray-700">{f.description}</p>
                  </div>
                ))}
              </div>
            )}

            {arbiter.suppressed_disagreements?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase text-blue-600 mb-1">Onopgeloste meningsverschillen</p>
                {arbiter.suppressed_disagreements.map((d, i) => (
                  <div key={`sup-${i}`} className="text-xs mb-1 pl-2 border-l-2 border-blue-300">
                    <span className="text-[10px] font-mono text-gray-500">
                      {(d.between || []).map(roleName).join(" ↔ ")}:
                    </span> {d.topic}
                    {d.why_unresolved && <p className="text-[11px] text-gray-600 mt-0.5">↳ {d.why_unresolved}</p>}
                  </div>
                ))}
              </div>
            )}

            {arbiter.hidden_assumptions?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase text-gray-600 mb-1">Verborgen aannames</p>
                <ul className="text-xs space-y-1">
                  {arbiter.hidden_assumptions.map((a, i) => (
                    <li key={`ass-${i}`} className="pl-2 border-l-2 border-gray-300">{a}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
    </section>
  );
};
